<script lang="ts" setup>
import { ref, computed } from 'vue';

const props = defineProps<{
  rooms: Array<{
    name: string;
    hotspots: {
      hotspots: Array<{
        x: string;
        y: string;
        title: string;
        description?: string;
        zoomThumbnail?: {
          mediaItemUrl: string;
        };
      }>;
      image: {
        mediaItemUrl: string;
        mediaDetails: {
          sizes: {
            sourceUrl: string,
            width: string,
            height: string,
            name: string
          }[]
        }
      };
    };
  }>;
  title?: string;
}>();

const selectedRoomIndex = ref(0);

const currentRoom = computed(() => {
  if (props.rooms && props.rooms.length > 0) {
    return props.rooms[selectedRoomIndex.value];
  }
  return null;
});

const selectRoom = (index: number) => {
  selectedRoomIndex.value = index;
};

</script>

<template>
  <div class="rooms-section">

    <!-- Left Sidebar - Room Thumbnails -->
    <div class="container-xl pe-0 d-flex d-md-none">
      <div class="rooms-sidebar mobile-rooms-sidebar">
        <div 
          v-for="(room, index) in rooms"
          class="pe-md-2 pe-0 pt-md-0 pt-2">

        <button
          :key="index"
          class="room-thumbnail"
          :class="{ active: selectedRoomIndex === index }"
          @click="selectRoom(index)"
          :aria-label="`View ${room.name}`"
        >
          <div class="thumbnail-image">
            <NuxtImg v-if="room.hotspots.image.mediaDetails.sizes.length > 0"
              :src="room.hotspots.image.mediaDetails.sizes[0].sourceUrl"
              :height="room.hotspots.image.mediaDetails.sizes[0].height"
              :width="room.hotspots.image.mediaDetails.sizes[0].width"
              :alt="room.name"
              loading="lazy"
              format="webp"
              :quality="75"
            />
            <NuxtImg v-else
              :src="room.hotspots.image.mediaItemUrl"
              :alt="room.name"
              loading="lazy"
              format="webp"
              :quality="75"
            />
          </div>
          <div class="thumbnail-label">
            <span class="room-name text-white">{{ room.name }}</span>
          </div>
        </button>

        </div>
      </div>
    </div>
    <div class="container-xl">
      <div class="rooms-layout">
        <!-- Left Sidebar - Room Thumbnails -->
        <div class="rooms-sidebar d-none d-md-flex">
          <div 
            v-for="(room, index) in rooms"
            class="pe-md-2 pe-0 pt-md-0 pt-2">

          <button
            :key="index"
            class="room-thumbnail"
            :class="{ active: selectedRoomIndex === index }"
            @click="selectRoom(index)"
            :aria-label="`View ${room.name}`"
          >
            <div class="thumbnail-image">
              <NuxtImg v-if="room.hotspots.image.mediaDetails.sizes.length > 0"
                :src="room.hotspots.image.mediaDetails.sizes[0].sourceUrl"
                :height="room.hotspots.image.mediaDetails.sizes[0].height"
                :width="room.hotspots.image.mediaDetails.sizes[0].width"
                :alt="room.name"
                loading="lazy"
                format="webp"
                :quality="75"
              />
              <NuxtImg v-else
                :src="room.hotspots.image.mediaItemUrl"
                :alt="room.name"
                loading="lazy"
                format="webp"
                :quality="75"
              />
            </div>
            <div class="thumbnail-label">
              <span class="room-name text-white">{{ room.name }}</span>
            </div>
          </button>

          </div>
        </div>

        <!-- Right Main Area - Hotspot Viewer -->
        <div class="rooms-main">
          <Transition name="scale-crossfade">
            <div v-if="currentRoom" :key="selectedRoomIndex" class="room-viewer">
              <HotspotViewer
                :image="currentRoom.hotspots.image.mediaItemUrl"
                :hotspots="currentRoom.hotspots.hotspots"
              />
            </div>
            <div v-else class="no-room">
              <p>No room data available</p>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/** start transition **/
.rooms-main {
  position: relative;
  min-height: 600px;
}

.room-viewer,
.no-room {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.scale-crossfade-enter-active {
  transition: all 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
  z-index: 2;
}

.scale-crossfade-leave-active {
  transition: all 0.35s ease-in;
  z-index: 1;
}

.scale-crossfade-enter-from {
  opacity: 0;
  transform: scale(0.97);
}

.scale-crossfade-leave-to {
  opacity: 0;
  transform: scale(1.03);
}

/*** end transition ***/

.thumbnail-image::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  pointer-events: none;
}

.rooms-section {
  background: #fff;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 600;
  margin-bottom: 40px;
  text-align: center;
  color: #1a1a1a;
}

.rooms-layout {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 30px;
  align-items: start;
}

.rooms-sidebar {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.room-thumbnail {
  background: var(--bs-secondary);
  border: none;
  border-radius: 15px;
  padding: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  text-align: center;
}

.room-thumbnail:hover {
  transform: translateX(5px);
}

.room-thumbnail.active {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background: var(--bs-color-light);
}

.thumbnail-image {
  width: 100%;
  overflow: hidden;
  position: relative;
  border-radius: 12px;
}

.thumbnail-image img {
  object-fit: cover;
  display: block;
}

.thumbnail-label {
  position: absolute;
  left: 50%;
  top: 50%;
  text-align: center;
  transform: translate(-50%, -50%);
}

.rooms-main {
  width: 100%;
  height: inherit;
  border-radius: 30px;
  overflow: hidden;
}

.room-viewer {
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.no-room {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 500px;
  color: #999;
  font-size: 1.1rem;
}

.rooms-layout {
  display: flex;
  height: 650px;
}

.rooms-sidebar {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
}

.room-thumbnail {
  flex: 0 0 auto;
  min-height: 100px; 
}

.rooms-sidebar {
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none; 
}

.rooms-sidebar::-webkit-scrollbar {
  display: none;
}

@media (max-width: 1024px) {
  .rooms-layout {
    grid-template-columns: 150px 1fr;
    gap: 20px;
  }

  .thumbnail-image {
    height: 100px;
  }

  .rooms-main {
    min-height: 400px;
  }

  .room-viewer {
    min-height: 400px;
  }
}

@media (max-width: 768px) {
  .mobile-rooms-sidebar {
    margin-bottom: 30px;
  }

  .section-title {
    font-size: 2rem;
    margin-bottom: 30px;
  }

  .rooms-layout {
    grid-template-columns: 1fr;
    gap: 10px;
    display: flex;
    flex-direction: column;
    height: unset;
  }

  .rooms-sidebar {
    flex-direction: row;
    overflow-x: auto;
    gap: 20px;
    padding-bottom: 10px;
    max-width: 100%;
    -webkit-overflow-scrolling: touch;
  }

  .room-thumbnail {
    min-width: 150px;
    flex-shrink: 0;
  }

  .room-thumbnail:hover {
    transform: translateY(-3px);
  }

  .thumbnail-image {
    height: 100px;
  }

  .rooms-main {
    min-height: 350px;
  }

  .room-viewer {
    min-height: 350px;
  }

  .no-room {
    height: 350px;
  }
}

@media (max-width: 480px) {
  .section-title {
    font-size: 1.75rem;
  }

  .room-thumbnail {
    min-width: 150px;
  }

  .thumbnail-image {
    height: 85px;
  }

  .room-name {
    font-size: 0.85rem;
  }
}
</style>
